'use client';

import type { PropertyExplanationArticleProps } from '@coolblue-development/next-hemingway/client';
import { createContext, ReactNode, useContext, useState } from 'react';

import { Facet } from '../types';
import { FilterPageContextProps } from './types';

type PropertyExplanationArticleWithTitle =
  | (PropertyExplanationArticleProps & {
      title: string;
    })
  | null;

type DrawerContextType = {
  clearFilters: (() => void) | undefined;
  drawerContent: (FilterPageContextProps & { facets: Facet[] }) | null;
  setDrawerContent: (
    content: (FilterPageContextProps & { facets: Facet[]; productCount: number }) | null
  ) => void;
  hasAppliedFilters: boolean;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  isPending: boolean;
  productCount: number;
  propertyExplanation: PropertyExplanationArticleWithTitle;
  setPropertyExplanation: (article: PropertyExplanationArticleWithTitle) => void;
};

const DrawerContext = createContext<DrawerContextType | undefined>(undefined);

export const DrawerProvider = ({ children }: { children: ReactNode }) => {
  const [drawerContent, setDrawerContent] = useState<
    (FilterPageContextProps & { facets: Facet[] }) | null
  >(null);
  const [isOpen, setIsOpen] = useState(false);
  const [propertyExplanation, setPropertyExplanation] =
    useState<PropertyExplanationArticleWithTitle>(null);

  return (
    <DrawerContext.Provider
      value={{
        clearFilters: drawerContent?.clearFilters,
        drawerContent,
        setDrawerContent,
        hasAppliedFilters: (drawerContent?.filters?.length ?? 0) > 0,
        isOpen,
        setIsOpen,
        isPending: drawerContent?.isPending ?? false,
        productCount: drawerContent?.productCount ?? 0,
        propertyExplanation,
        setPropertyExplanation
      }}
    >
      {children}
    </DrawerContext.Provider>
  );
};

export const useFilterDrawer = () => {
  const context = useContext(DrawerContext);

  if (context === undefined) {
    throw new Error('useFilterDrawer must be used within a DrawerProvider');
  }

  return context;
};
